/**
 * מסך תודה — TASKS 5.2: "מספר הצעה + המספר המלא + כפתור שיחה. ⛔ ללא סכום."
 *
 * ⭐ **מרוקן את העגלה כאן, ⛔ לא ב-`RequestPage`** — כדי שבדיקת "עגלה ריקה →
 *    חזרה ל-/cart" ב-`RequestPage` לא תתחרה עם הניווט לעמוד הזה (מירוץ בין
 *    ניקוי מיידי של state לבין ניתוב).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotionConfig } from 'motion/react';
import { Copy } from '@phosphor-icons/react';
// ⚠️ תת-נתיב, ⛔ לא הבארל `@noam/shared` — ראו ההערה ב-CartPage.tsx
import { buildClickToChatUrl } from '@noam/shared/whatsapp';
import { useCart } from '../cart/CartContext.js';

const STAGGER_SECONDS = 0.08;

function stepTransition(order: number) {
  return { duration: 0.3, delay: order * STAGGER_SECONDS };
}

/** ⭐ DESIGN.md 6.2 — "וי מצויר בקו SVG", 600ms. `pathLength` הוא ה-API של
 * `motion` לציור קו SVG (stroke שנחשף בהדרגה), ⛔ לא dasharray ידני. */
function DrawnCheckmark({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <svg viewBox="0 0 52 52" width="64" height="64" aria-hidden="true" className="mx-auto">
      <motion.circle
        cx="26"
        cy="26"
        r="23"
        fill="none"
        stroke="var(--color-copper)"
        strokeWidth="2"
        initial={reducedMotion ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={reducedMotion ? { duration: 0 } : { duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.path
        d="M15 27l7.5 7.5L37 19"
        fill="none"
        stroke="var(--color-copper)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={reducedMotion ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={
          reducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }
        }
      />
    </svg>
  );
}

interface ThankYouState {
  quoteNumber: string;
  phoneDisplay: string;
  whatsappBusinessPhone: string;
}

function isThankYouState(value: unknown): value is ThankYouState {
  const v = value as Partial<ThankYouState> | null;
  return Boolean(
    v && typeof v.quoteNumber === 'string' && typeof v.whatsappBusinessPhone === 'string',
  );
}

export function ThankYouPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const cart = useCart();
  const reducedMotion = Boolean(useReducedMotionConfig());
  const [copied, setCopied] = useState(false);

  const state = isThankYouState(location.state) ? location.state : null;

  // ⭐ DESIGN.md 5.7 — "מספר ההצעה... ניתן להעתקה בלחיצה".
  // ⚠️ `navigator.clipboard` יכול להיות `undefined` (הקשר לא-מאובטח/דפדפן ישן) —
  //    ⛔ בלי guard, קריאה ל-`.then` על `undefined` הייתה קורסת.
  function handleCopyQuoteNumber() {
    if (!state || !navigator.clipboard) return;
    navigator.clipboard.writeText(state.quoteNumber).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }

  useEffect(() => {
    cart.clearCart();
    // ⚠️ ⛔ פעם אחת בעלייה בלבד — ⛔ לא תלוי ב-cart (שמשתנה בעצם הריקון)
  }, []);

  if (!state) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">הבקשה נשלחה</h1>
        <p className="text-muted mt-3">אפשר לסגור את העמוד הזה או לחזור לדף הבית.</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="btn btn-secondary mt-6 inline-flex"
        >
          חזרה לדף הבית
        </button>
      </div>
    );
  }

  const waHref = buildClickToChatUrl(
    state.whatsappBusinessPhone,
    `שלום, אני מעוניין/ת בהצעת מחיר ${state.quoteNumber}`,
  );

  return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center">
      {/* ⭐ TASKS 6.2 · DESIGN.md 6.2 — stagger 80ms: וי -> מספר הצעה -> כפתורים */}
      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stepTransition(0)}
      >
        <DrawnCheckmark reducedMotion={reducedMotion} />
        <h1 className="mt-4 text-3xl font-bold">קיבלנו את בקשתכם!</h1>
      </motion.div>

      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stepTransition(1)}
      >
        <p className="mt-4">מספר הבקשה שלכם:</p>
        <button
          type="button"
          onClick={handleCopyQuoteNumber}
          className="ltr-inline tabular text-ink mt-1 inline-flex items-center gap-2 text-2xl font-bold"
          aria-label={`העתקת מספר הבקשה ${state.quoteNumber}`}
        >
          {state.quoteNumber}
          <Copy
            size={20}
            weight="regular"
            aria-hidden="true"
            style={{ color: 'var(--color-copper)' }}
          />
        </button>
        <p role="status" aria-live="polite" className="text-link h-5 text-sm">
          {copied ? 'המספר הועתק' : ''}
        </p>
        <p className="text-muted mt-2">
          הצעת המחיר המלאה בדרך אליכם בדוא"ל. אפשר גם לפנות אלינו ישירות בוואטסאפ.
        </p>
      </motion.div>

      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stepTransition(2)}
      >
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-cta mt-6 inline-flex"
        >
          דברו איתנו בוואטסאפ
        </a>
      </motion.div>
      <p className="mt-6">
        <Link to="/" className="text-link underline">
          חזרה לדף הבית
        </Link>
      </p>
    </div>
  );
}
